"use client";

import { useState, useEffect } from "react";
import {
  faBars,
  faHome,
  faUtensils,
  faSpa,
  faShoppingBag,
} from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link, useNavigate } from "react-router-dom";
import logo from "@/assets/img/logo.png";
import { motion, AnimatePresence } from "motion/react";
import { Dock, DockIcon } from "@/components/ui/dock";
import { useFilter } from "@/contexts/FilterContext";

interface LandingHeaderProps {
  className?: string;
}

type BusinessType = "All" | "Restaurant" | "Services" | "Retail";

interface NavItem {
  id: string;
  name: string;
  icon: any; // FontAwesome icon type
  type: BusinessType;
}

export default function LandingHeader({ className }: LandingHeaderProps) {
  const navigate = useNavigate();
  const { businessType, setBusinessType } = useFilter();
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [hasScrolled, setHasScrolled] = useState(false);

  // Navigation items
  const navItems: NavItem[] = [
    { id: "all", name: "All", icon: faHome, type: "All" },
    {
      id: "restaurants",
      name: "Restaurants",
      icon: faUtensils,
      type: "Restaurant",
    },
    { id: "services", name: "Services", icon: faSpa, type: "Services" },
    { id: "retail", name: "Retail", icon: faShoppingBag, type: "Retail" },
  ];

  // Handle scroll effects
  useEffect(() => {
    const handleScroll = () => {
      setHasScrolled(window.scrollY > 10);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  // Handle window resize to update mobile state
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Disable/enable scroll on body when drawer is open
  useEffect(() => {
    if (isDrawerOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "auto";
    }
    // Cleanup on unmount
    return () => {
      document.body.style.overflow = "auto";
    };
  }, [isDrawerOpen]);

  const toggleDrawer = () => setIsDrawerOpen(!isDrawerOpen);

  // Navigate to home
  const handleHomeClick = () => {
    setBusinessType("All");
    navigate("/");
  };

  // Handle filter selection
  const handleFilterSelect = (type: BusinessType) => {
    setBusinessType(type);
    setIsDrawerOpen(false);
  };

  // Close drawer when clicking overlay
  const handleOverlayClick = () => {
    setIsDrawerOpen(false);
  };

  return (
    <>
      <header
        className={`sticky z-40 flex justify-center transition-all duration-300 ${
          isMobile
            ? hasScrolled
              ? "top-1 mx-0 px-0 w-full"
              : "top-1 mx-0 px-0 w-full"
            : hasScrolled
              ? "top-6 mx-4"
              : "top-4 mx-0"
        } ${className || ""}`}
      >
        <motion.div
          initial={{ width: isMobile ? "100%" : "70rem" }}
          animate={{
            width: isMobile ? "100%" : hasScrolled ? "1000px" : "70rem",
          }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
          className="w-full md:w-auto"
        >
          <div
            className={`mx-auto w-full max-w-7xl rounded-2xl transition-all duration-300 xl:px-0 ${
              hasScrolled
                ? isMobile
                  ? "px-0 border border-gray-200 backdrop-blur-lg bg-white/75"
                  : "px-6 border border-gray-200 backdrop-blur-lg bg-white/75"
                : isMobile
                  ? "shadow-none px-0"
                  : "shadow-none px-7 md:px-7"
            }`}
          >
            <div
              className={`flex h-[56px] md:h-[56px] items-center justify-between ${isMobile ? "px-1" : "p-2 md:p-4"}`}
            >
              {/* Logo */}
              <div
                className="flex items-center gap-2 md:gap-3 cursor-pointer pl-5 md:pl-2"
                onClick={handleHomeClick}
              >
                <img src={logo} alt="Redbird Logo" className="h-8 md:h-12" />
              </div>

              {/* Desktop Navigation */}
              <div className="hidden md:flex items-center space-x-8">
                {navItems.slice(1).map((item) => (
                  <button
                    key={item.id}
                    onClick={() => handleFilterSelect(item.type)}
                    className={`text-base font-medium transition-colors ${
                      businessType === item.type
                        ? "text-redCrimson"
                        : "text-gray-700 hover:text-redCrimson"
                    }`}
                  >
                    {item.name}
                  </button>
                ))}
              </div>

              {/* Mobile Menu Button */}
              <div className="md:hidden flex items-center pr-5">
                <button
                  onClick={toggleDrawer}
                  className="flex items-center justify-center h-11 w-11 rounded-full bg-gray-100 hover:bg-gray-200"
                >
                  <FontAwesomeIcon
                    icon={faBars}
                    className="text-xl text-gray-700"
                  />
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      </header>

      {/* Mobile Drawer Overlay */}
      <AnimatePresence>
        {isDrawerOpen && (
          <>
            <motion.div
              className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-40"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.2 }}
              onClick={handleOverlayClick}
            />

            {/* Mobile Drawer - Opening from top */}
            <motion.div
              className="fixed inset-x-0 top-0 w-full bg-white border-b border-gray-200 p-4 shadow-lg z-50"
              initial={{ opacity: 0, y: -300 }}
              animate={{
                opacity: 1,
                y: 0,
                transition: {
                  type: "spring",
                  damping: 25,
                  stiffness: 300,
                },
              }}
              exit={{
                opacity: 0,
                y: -300,
                transition: { duration: 0.2 },
              }}
            >
              {/* Drawer Content */}
              <div className="flex flex-col gap-4">
                <div className="flex items-center justify-between">
                  <div
                    className="flex items-center gap-2"
                    onClick={handleHomeClick}
                  >
                    <img src={logo} alt="Redbird Logo" className="h-8" />
                  </div>
                  <button
                    onClick={toggleDrawer}
                    className="border border-gray-200 rounded-md p-1 cursor-pointer"
                  >
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      className="h-5 w-5"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M6 18L18 6M6 6l12 12"
                      />
                    </svg>
                  </button>
                </div>

                {/* Navigation Links */}
                <div className="grid grid-cols-2 gap-2 mt-2">
                  {navItems.map((item) => (
                    <button
                      key={item.id}
                      className={`flex items-center gap-2 p-3 rounded-lg ${
                        businessType === item.type
                          ? "bg-redCrimson/10 text-redCrimson"
                          : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                      }`}
                      onClick={() => handleFilterSelect(item.type)}
                    >
                      <FontAwesomeIcon icon={item.icon} className="text-lg" />
                      <span className="font-medium">{item.name}</span>
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>
          </>
        )}
      </AnimatePresence>

      {/* Mobile Dock Navigation */}
      {isMobile && (
        <div className="fixed bottom-4 left-0 right-0 flex justify-center z-50">
          <Dock className="bg-white/90 border border-gray-200 shadow-lg rounded-full">
            {navItems.map((item) => (
              <DockIcon
                key={item.id}
                className={`${
                  businessType === item.type
                    ? "bg-redCrimson/10 text-redCrimson"
                    : "bg-gray-100 hover:bg-gray-200"
                }`}
                onClick={() => handleFilterSelect(item.type)}
                size={45}
                magnification={60}
              >
                <FontAwesomeIcon
                  icon={item.icon}
                  className={`text-2xl ${
                    businessType === item.type
                      ? "text-redCrimson"
                      : "text-gray-700"
                  }`}
                />
              </DockIcon>
            ))}
          </Dock>
        </div>
      )}
    </>
  );
}
